/**
 * TilePixelSampler.ts
 *
 * High-performance client-side tile sampler and meteorological color inversion service.
 * Ingests raster tile URLs (PMTiles or WebP) and performs zero-overhead, sub-millisecond
 * pixel lookups on raw unblended tile bitmaps to recover exact temperature values.
 */

import { getOrCreatePMTiles, buildPmtilesUrl, GLOBAL_COORDINATES, STANDARD_NA_COORDINATES } from '../config';
import imageBitmapCache, { isAlreadyMercatorProjected } from '../utils/ImageBitmapCache';

export function applyTempGradient(celsius: number): [number, number, number] {
  if (celsius <= -20) {
    return [100, 0, 150];
  } else if (celsius <= -10) {
    const t = (celsius + 20) / 10.0;
    return [Math.round(100 * (1 - t)), 0, Math.round(150 + 105 * t)];
  } else if (celsius <= 0) {
    const t = (celsius + 10) / 10.0;
    return [0, Math.round(240 * t), 255];
  } else if (celsius <= 10) {
    const t = celsius / 10.0;
    return [0, Math.round(240 + 15 * t), Math.round(255 * (1 - t))];
  } else if (celsius <= 20) {
    const t = (celsius - 10) / 10.0;
    return [Math.round(255 * t), 255, 0];
  } else if (celsius <= 30) {
    const t = (celsius - 20) / 10.0;
    return [255, Math.round(255 - 127 * t), 0];
  } else if (celsius < 40) {
    const t = (celsius - 30) / 10.0;
    return [255, Math.round(128 * (1 - t)), 0];
  } else {
    return [255, 0, 0];
  }
}

interface LutEntry {
  celsius: number;
  r: number;
  g: number;
  b: number;
}

// Precomputed lookup table for continuous piecewise colormap inversion (-30°C to +45°C)
const TEMP_LUT: LutEntry[] = (() => {
  const lut: LutEntry[] = [];
  for (let c = -30; c <= 45; c += 0.25) {
    const [r, g, b] = applyTempGradient(c);
    lut.push({ celsius: c, r, g, b });
  }
  return lut;
})();

/**
 * Inverts an RGB pixel value to the nearest corresponding temperature in Celsius.
 * Returns null if the color falls outside valid meteorological gradient thresholds.
 */
export function invertTempColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestCelsius: number | null = null;

  for (let i = 0; i < TEMP_LUT.length; i++) {
    const entry = TEMP_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestCelsius = entry.celsius;
      if (distSq === 0) break;
    }
  }

  // Reject colors not belonging to the temperature gradient
  if (minDistance > 550) {
    return null;
  }

  return bestCelsius;
}

/**
 * Freezing Level altitude color palette (0 ft to 22,000 ft MSL):
 * 0 ft:       #8b5cf6 (139, 92, 246)
 * 3,000 ft:   #3b82f6 (59, 130, 246)
 * 6,000 ft:   #06b6d4 (6, 182, 212)
 * 9,000 ft:   #10b981 (16, 185, 129)
 * 12,000 ft:  #eab308 (234, 179, 8)
 * 15,000 ft:  #f97316 (249, 115, 22)
 * 18,000 ft:  #ef4444 (239, 68, 68)
 * 22,000 ft+: #b91c1c (185, 28, 28)
 */
export function applyFreezingLevelGradient(elevFt: number): [number, number, number] {
  const elevStops = [0.0, 3000.0, 6000.0, 9000.0, 12000.0, 15000.0, 18000.0, 22000.0];
  const rStops = [139, 59, 6, 16, 234, 249, 239, 185];
  const gStops = [92, 130, 182, 185, 179, 115, 68, 28];
  const bStops = [246, 246, 212, 129, 8, 22, 68, 28];

  if (elevFt <= 0) {
    return [rStops[0], gStops[0], bStops[0]];
  }
  if (elevFt >= 22000) {
    return [rStops[7], gStops[7], bStops[7]];
  }

  for (let i = 0; i < elevStops.length - 1; i++) {
    if (elevFt <= elevStops[i + 1]) {
      const t = (elevFt - elevStops[i]) / (elevStops[i + 1] - elevStops[i]);
      const r = Math.round(rStops[i] + t * (rStops[i + 1] - rStops[i]));
      const g = Math.round(gStops[i] + t * (gStops[i + 1] - gStops[i]));
      const b = Math.round(bStops[i] + t * (bStops[i + 1] - bStops[i]));
      return [r, g, b];
    }
  }

  return [rStops[7], gStops[7], bStops[7]];
}

interface FreezingLutEntry {
  elevFt: number;
  r: number;
  g: number;
  b: number;
}

// Precomputed lookup table for continuous freezing level elevation inversion (0 ft to 24,000 ft in 50-ft increments)
const FREEZING_LUT: FreezingLutEntry[] = (() => {
  const lut: FreezingLutEntry[] = [];
  for (let ft = 0; ft <= 24000; ft += 50) {
    const [r, g, b] = applyFreezingLevelGradient(ft);
    lut.push({ elevFt: ft, r, g, b });
  }
  return lut;
})();

/**
 * Inverts an RGB pixel value to the nearest corresponding freezing level elevation in feet.
 * Returns null if the color falls outside valid freezing level gradient thresholds.
 */
export function invertFreezingLevelColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestElev: number | null = null;

  for (let i = 0; i < FREEZING_LUT.length; i++) {
    const entry = FREEZING_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestElev = entry.elevFt;
      if (distSq === 0) break;
    }
  }

  // Reject colors not belonging to the freezing level gradient
  if (minDistance > 1000) {
    return null;
  }

  return bestElev;
}

export function applyAqiGradient(score: number): [number, number, number] {
  if (score <= 0) return [0, 228, 0];
  if (score < 50) {
    const t = score / 50.0;
    return [Math.round(255 * t), Math.round(228 + 27 * t), 0];
  }
  if (score < 100) {
    const t = (score - 50) / 50.0;
    return [255, Math.round(255 - 129 * t), 0];
  }
  if (score < 150) {
    const t = (score - 100) / 50.0;
    return [255, Math.round(126 - 126 * t), 0];
  }
  if (score < 200) {
    const t = (score - 150) / 50.0;
    return [Math.round(255 - 112 * t), Math.round(63 * t), Math.round(151 * t)];
  }
  if (score < 300) {
    const t = (score - 200) / 100.0;
    return [Math.round(143 - 17 * t), Math.round(63 - 63 * t), Math.round(151 - 116 * t)];
  }
  return [126, 0, 35];
}

export function applyAqhiGradient(score: number): [number, number, number] {
  if (score <= 1.0) return [0, 180, 255];
  if (score < 3.5) {
    const t = (score - 1.0) / 2.5;
    return [0, Math.round(180 + 40 * t), 255];
  }
  if (score < 6.5) {
    const t = (score - 3.5) / 3.0;
    return [255, Math.round(220 - 100 * t), 0];
  }
  if (score < 10.0) {
    const t = (score - 6.5) / 3.5;
    return [Math.round(255 - 105 * t), 0, 0];
  }
  return [100, 0, 0];
}

interface AqiLutEntry {
  aqi: number;
  r: number;
  g: number;
  b: number;
}

const AQI_LUT: AqiLutEntry[] = (() => {
  const lut: AqiLutEntry[] = [];
  for (let s = 0; s <= 500; s += 1) {
    const [r, g, b] = applyAqiGradient(s);
    lut.push({ aqi: s, r, g, b });
  }
  return lut;
})();

interface AqhiLutEntry {
  aqhi: number;
  r: number;
  g: number;
  b: number;
}

const AQHI_LUT: AqhiLutEntry[] = (() => {
  const lut: AqhiLutEntry[] = [];
  for (let s = 10; s <= 120; s += 1) {
    const score = s / 10.0;
    const [r, g, b] = applyAqhiGradient(score);
    lut.push({ aqhi: score, r, g, b });
  }
  return lut;
})();

export function invertAqiColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestAqi: number | null = null;

  for (let i = 0; i < AQI_LUT.length; i++) {
    const entry = AQI_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestAqi = entry.aqi;
      if (distSq === 0) break;
    }
  }

  if (minDistance > 2500) {
    return null;
  }
  return bestAqi;
}

export function invertAqhiColor(r: number, g: number, b: number): number | null {
  // Defensive fallback for legacy flat-green raster tiles
  if (r < 50 && g > 150 && b < 50) {
    return 1.0;
  }

  let minDistance = Infinity;
  let bestAqhi: number | null = null;

  for (let i = 0; i < AQHI_LUT.length; i++) {
    const entry = AQHI_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestAqhi = entry.aqhi;
      if (distSq === 0) break;
    }
  }

  if (minDistance > 2500) {
    return null;
  }
  return bestAqhi;
}

// -------------------------------------------------------------
// Subjective Forecasts (Blend50: Sunrise, Sunset, Golden Hour, Alpenglow)
// -------------------------------------------------------------
export function applyBlend50Gradient(score: number): [number, number, number] {
  const s = Math.max(0, Math.min(100, score));
  if (s >= 95) {
    return [255, 0, 0];
  } else if (s >= 50) {
    const t = (s - 50) / 45.0;
    return [255, Math.round(255 * (1 - t)), 0];
  } else {
    const t = s / 50.0;
    return [Math.round(255 * t), Math.round(70 + 185 * t), Math.round(160 * (1 - t))];
  }
}

const BLEND50_LUT: { score: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 0; s <= 100; s += 1) {
    const [r, g, b] = applyBlend50Gradient(s);
    lut.push({ score: s, r, g, b });
  }
  return lut;
})();

export function invertBlend50Color(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestScore: number | null = null;
  for (let i = 0; i < BLEND50_LUT.length; i++) {
    const entry = BLEND50_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestScore = entry.score;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestScore;
}

// -------------------------------------------------------------
// Subjective Forecasts: Dark Skies (0.0 - 10.0)
// -------------------------------------------------------------
export function applyDarkSkiesGradient(score: number): [number, number, number] {
  const s = Math.max(0, Math.min(10, score));
  if (s < 2) {
    const t = s / 2.0;
    return [Math.round(180 + 75 * t), Math.round(128 * t), 0];
  } else if (s < 4) {
    const t = (s - 2) / 2.0;
    return [255, Math.round(128 + 127 * t), 0];
  } else if (s < 6) {
    const t = (s - 4) / 2.0;
    return [Math.round(255 * (1 - t)), 255, 0];
  } else if (s < 8) {
    const t = (s - 6) / 2.0;
    return [0, 255, Math.round(255 * t)];
  } else {
    const t = Math.min(1, (s - 8) / 2.0);
    return [0, Math.round(255 - 205 * t), 255];
  }
}

const DARKSKIES_LUT: { score: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 0; s <= 100; s += 1) {
    const val = s / 10.0;
    const [r, g, b] = applyDarkSkiesGradient(val);
    lut.push({ score: val, r, g, b });
  }
  return lut;
})();

export function invertDarkSkiesColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestScore: number | null = null;
  for (let i = 0; i < DARKSKIES_LUT.length; i++) {
    const entry = DARKSKIES_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestScore = entry.score;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestScore;
}

// -------------------------------------------------------------
// Relative Humidity (0 - 100%)
// -------------------------------------------------------------
export function applyRhGradient(score: number): [number, number, number] {
  const s = Math.max(0, Math.min(100, score));
  if (s < 30) {
    const t = s / 30.0;
    return [Math.round(215 + t * 36), Math.round(204 - t * 12), Math.round(200 - t * 155)];
  } else if (s < 70) {
    const t = (s - 30.0) / 40.0;
    return [Math.round(77 - t * 75), Math.round(208 - t * 72), Math.round(225 - t * 16)];
  } else {
    const t = Math.min(1, (s - 70.0) / 30.0);
    return [Math.round(2 + t * 11), Math.round(119 - t * 48), Math.round(189 - t * 28)];
  }
}

const RH_LUT: { rh: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 0; s <= 100; s += 1) {
    const [r, g, b] = applyRhGradient(s);
    lut.push({ rh: s, r, g, b });
  }
  return lut;
})();

export function invertRhColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestRh: number | null = null;
  for (let i = 0; i < RH_LUT.length; i++) {
    const entry = RH_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestRh = entry.rh;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestRh;
}

// -------------------------------------------------------------
// Dew Point Comfort Gradient (Celsius: -15 to +30)
// -------------------------------------------------------------
export function applyDewpointGradient(celsius: number): [number, number, number] {
  if (celsius <= -1.0) {
    const t = Math.max(0, Math.min(1, (celsius + 15.0) / 14.0));
    return [Math.round(145 - t * 45), Math.round(160 + t * 35), Math.round(220 - t * 5)];
  } else if (celsius <= 10.0) {
    const t = (celsius + 1.0) / 11.0;
    return [Math.round(100 - t * 30), Math.round(195 + t * 10), Math.round(215 - t * 100)];
  } else if (celsius <= 15.5) {
    const t = (celsius - 10.0) / 5.5;
    return [Math.round(70 + t * 125), Math.round(205 + t * 10), Math.round(115 - t * 75)];
  } else if (celsius <= 18.3) {
    const t = (celsius - 15.5) / 2.8;
    return [Math.round(240 + t * 10), Math.round(215 - t * 50), Math.round(30 - t * 10)];
  } else if (celsius <= 21.1) {
    const t = (celsius - 18.3) / 2.8;
    return [Math.round(250 - t * 5), Math.round(140 - t * 65), Math.round(20 + t * 20)];
  } else if (celsius <= 24.0) {
    const t = (celsius - 21.1) / 2.9;
    return [Math.round(235 - t * 55), Math.round(30 - t * 15), Math.round(40 + t * 20)];
  } else {
    const t = Math.min(1, (celsius - 24.0) / 6.0);
    return [Math.round(180 - t * 40), Math.round(15 - t * 15), Math.round(60 + t * 70)];
  }
}

const DEWPOINT_LUT: { celsius: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let c = -15; c <= 30; c += 0.5) {
    const [r, g, b] = applyDewpointGradient(c);
    lut.push({ celsius: c, r, g, b });
  }
  return lut;
})();

export function invertDewpointColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestCelsius: number | null = null;
  for (let i = 0; i < DEWPOINT_LUT.length; i++) {
    const entry = DEWPOINT_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestCelsius = entry.celsius;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestCelsius;
}

// -------------------------------------------------------------
// Valley Fog Probability (0 - 100%)
// -------------------------------------------------------------
export function applyFogGradient(score: number): [number, number, number] {
  if (score < 40) {
    const t = Math.max(0, (score - 15.0) / 25.0);
    return [Math.round(100 + t * 65), Math.round(130 + t * 55), Math.round(150 + t * 45)];
  } else if (score < 70) {
    const t = (score - 40.0) / 30.0;
    return [Math.round(185 + t * 50), Math.round(200 + t * 42), Math.round(210 + t * 35)];
  } else {
    const t = Math.min(1, (score - 70.0) / 30.0);
    return [Math.round(240 + t * 15), Math.round(245 + t * 10), Math.round(245 + t * 10)];
  }
}

const FOG_LUT: { score: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 15; s <= 100; s += 1) {
    const [r, g, b] = applyFogGradient(s);
    lut.push({ score: s, r, g, b });
  }
  return lut;
})();

export function invertFogColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestScore: number | null = null;
  for (let i = 0; i < FOG_LUT.length; i++) {
    const entry = FOG_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestScore = entry.score;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestScore;
}

// -------------------------------------------------------------
// Optics Dew Risk (0 - 100)
// -------------------------------------------------------------
export function applyOpticsDewRiskGradient(score: number): [number, number, number] {
  if (score < 35.0) {
    const t = Math.max(0, (score - 10.0) / 25.0);
    return [Math.round(t * 50), Math.round(150 + t * 50), Math.round(100 + t * 50)];
  } else if (score < 70.0) {
    const t = (score - 35.0) / 35.0;
    return [Math.round(230 + t * 25), Math.round(180 - t * 80), Math.round(30 + t * 20)];
  } else {
    const t = Math.min(1, (score - 70.0) / 30.0);
    return [Math.round(240 - t * 40), Math.round(40 - t * 30), Math.round(80 + t * 100)];
  }
}

const DEW_RISK_LUT: { score: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 10; s <= 100; s += 1) {
    const [r, g, b] = applyOpticsDewRiskGradient(s);
    lut.push({ score: s, r, g, b });
  }
  return lut;
})();

export function invertOpticsDewRiskColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestScore: number | null = null;
  for (let i = 0; i < DEW_RISK_LUT.length; i++) {
    const entry = DEW_RISK_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestScore = entry.score;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestScore;
}

// -------------------------------------------------------------
// Seeing Quality (0 - 100)
// -------------------------------------------------------------
export function applySeeingGradient(score: number): [number, number, number] {
  if (score >= 80) {
    const t = (score - 80) / 20.0;
    return [Math.round(30 - t * 10), Math.round(58 + t * 20), Math.round(138 + t * 80)];
  } else if (score >= 60) {
    const t = (score - 60) / 20.0;
    return [Math.round(5 + t * 25), Math.round(150 - t * 92), Math.round(105 + t * 33)];
  } else if (score >= 40) {
    const t = (score - 40) / 20.0;
    return [Math.round(132 - t * 127), Math.round(204 - t * 54), Math.round(22 + t * 83)];
  } else if (score >= 20) {
    const t = (score - 20) / 20.0;
    return [Math.round(217 - t * 85), Math.round(119 + t * 85), Math.round(6 + t * 16)];
  } else {
    const t = score / 20.0;
    return [Math.round(147 + t * 70), Math.round(51 + t * 68), Math.round(234 - t * 228)];
  }
}

const SEEING_LUT: { score: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let s = 0; s <= 100; s += 1) {
    const [r, g, b] = applySeeingGradient(s);
    lut.push({ score: s, r, g, b });
  }
  return lut;
})();

export function invertSeeingColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestScore: number | null = null;
  for (let i = 0; i < SEEING_LUT.length; i++) {
    const entry = SEEING_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestScore = entry.score;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestScore;
}

// -------------------------------------------------------------
// Aviation Turbulence (ICAO EDR 0.08 - 1.00)
// -------------------------------------------------------------
const TURBULENCE_STOPS = [0.08, 0.12, 0.20, 0.32, 0.45, 0.60, 0.75, 1.00];
const TURBULENCE_R = [46, 46, 241, 230, 231, 192, 155, 240];
const TURBULENCE_G = [204, 204, 196, 126, 76, 57, 89, 50];
const TURBULENCE_B = [113, 113, 18, 34, 60, 43, 182, 240];

function applyTurbulenceGradient(edr: number): [number, number, number] {
  if (edr <= TURBULENCE_STOPS[0]) return [TURBULENCE_R[0], TURBULENCE_G[0], TURBULENCE_B[0]];
  const last = TURBULENCE_STOPS.length - 1;
  if (edr >= TURBULENCE_STOPS[last]) return [TURBULENCE_R[last], TURBULENCE_G[last], TURBULENCE_B[last]];
  for (let i = 0; i < last; i++) {
    if (edr >= TURBULENCE_STOPS[i] && edr <= TURBULENCE_STOPS[i + 1]) {
      const t = (edr - TURBULENCE_STOPS[i]) / (TURBULENCE_STOPS[i + 1] - TURBULENCE_STOPS[i]);
      return [
        Math.round(TURBULENCE_R[i] + t * (TURBULENCE_R[i + 1] - TURBULENCE_R[i])),
        Math.round(TURBULENCE_G[i] + t * (TURBULENCE_G[i + 1] - TURBULENCE_G[i])),
        Math.round(TURBULENCE_B[i] + t * (TURBULENCE_B[i + 1] - TURBULENCE_B[i]))
      ];
    }
  }
  return [TURBULENCE_R[0], TURBULENCE_G[0], TURBULENCE_B[0]];
}

const TURBULENCE_LUT: { edr: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let step = 8; step <= 100; step += 1) {
    const edr = step / 100;
    const [r, g, b] = applyTurbulenceGradient(edr);
    lut.push({ edr, r, g, b });
  }
  return lut;
})();

export function invertTurbulenceColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestEdr: number | null = null;
  for (let i = 0; i < TURBULENCE_LUT.length; i++) {
    const entry = TURBULENCE_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestEdr = entry.edr;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestEdr;
}

// -------------------------------------------------------------
// CAPE (0 - 5000 J/kg)
// -------------------------------------------------------------
export function applyCapeGradient(cape: number): [number, number, number] {
  if (cape < 500) {
    const t = Math.max(0, (cape - 100) / 400.0);
    return [Math.round(t * 40), Math.round(200 + t * 40), Math.round(255 - t * 50)];
  } else if (cape < 1000) {
    const t = (cape - 500) / 500.0;
    return [Math.round(40 + t * 215), Math.round(240 - t * 6), Math.round(205 - t * 205)];
  } else if (cape < 2000) {
    const t = (cape - 1000) / 1000.0;
    return [255, Math.round(234 - t * 89), 0];
  } else if (cape < 3500) {
    const t = (cape - 2000) / 1500.0;
    return [Math.round(255 - t * 42), Math.round(145 - t * 145), 0];
  } else {
    const t = Math.min(1, (cape - 3500) / 1500.0);
    return [Math.round(213 - t * 139), Math.round(t * 20), Math.round(249 - t * 109)];
  }
}

const CAPE_LUT: { cape: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let c = 100; c <= 5000; c += 50) {
    const [r, g, b] = applyCapeGradient(c);
    lut.push({ cape: c, r, g, b });
  }
  return lut;
})();

export function invertCapeColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestCape: number | null = null;
  for (let i = 0; i < CAPE_LUT.length; i++) {
    const entry = CAPE_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestCape = entry.cape;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestCape;
}

// -------------------------------------------------------------
// Thunderstorm Probability (0 - 100%)
// -------------------------------------------------------------
export function applyThunderstormGradient(prob: number): [number, number, number] {
  if (prob < 35) {
    const t = Math.max(0, (prob - 15) / 20.0);
    return [Math.round(t * 118), Math.round(229 + t * 26), Math.round(255 - t * 252)];
  } else if (prob < 65) {
    const t = (prob - 35) / 30.0;
    return [Math.round(118 + t * 137), Math.round(255 - t * 110), 0];
  } else {
    const t = Math.min(1, (prob - 65) / 35.0);
    return [Math.round(255 - t * 105), Math.round(145 - t * 145), Math.round(t * 150)];
  }
}

const THUNDERSTORM_LUT: { prob: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let p = 15; p <= 100; p += 1) {
    const [r, g, b] = applyThunderstormGradient(p);
    lut.push({ prob: p, r, g, b });
  }
  return lut;
})();

export function invertThunderstormColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestProb: number | null = null;
  for (let i = 0; i < THUNDERSTORM_LUT.length; i++) {
    const entry = THUNDERSTORM_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestProb = entry.prob;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestProb;
}

// -------------------------------------------------------------
// UV Index (0 - 14+)
// -------------------------------------------------------------
export function applyUvGradient(uv: number): [number, number, number] {
  if (uv < 3) {
    const t = uv / 3.0;
    return [Math.round(76 + t * 175), Math.round(175 + t * 17), Math.round(80 - t * 35)];
  } else if (uv < 6) {
    const t = (uv - 3) / 3.0;
    return [Math.round(251 - t * 6), Math.round(192 - t * 68), Math.round(45 - t * 45)];
  } else if (uv < 8) {
    const t = (uv - 6) / 2.0;
    return [Math.round(245 - t * 34), Math.round(124 - t * 77), 0];
  } else if (uv < 11) {
    const t = (uv - 8) / 3.0;
    return [Math.round(211 - t * 88), Math.round(47 - t * 16), Math.round(47 + t * 115)];
  } else {
    const t = Math.min(1, (uv - 11) / 3.0);
    return [Math.round(123 + t * 47), Math.round(31 - t * 31), Math.round(162 + t * 93)];
  }
}

const UV_LUT: { uv: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let u = 0; u <= 140; u += 1) {
    const val = u / 10.0;
    const [r, g, b] = applyUvGradient(val);
    lut.push({ uv: val, r, g, b });
  }
  return lut;
})();

export function invertUvColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestUv: number | null = null;
  for (let i = 0; i < UV_LUT.length; i++) {
    const entry = UV_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestUv = entry.uv;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestUv;
}

// -------------------------------------------------------------
// Sea Surface Temperature (SST in Fahrenheit: 28°F to 98°F)
// -------------------------------------------------------------
const SST_STOPS: [number, [number, number, number]][] = [
  [28.0, [11, 19, 43]],
  [40.0, [28, 37, 65]],
  [50.0, [58, 80, 107]],
  [60.0, [91, 192, 190]],
  [70.0, [72, 149, 239]],
  [80.0, [255, 158, 0]],
  [90.0, [217, 4, 41]],
  [98.0, [157, 2, 8]]
];

const SST_LUT: { fahrenheit: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let f = 28; f <= 98; f += 0.5) {
    let r = 0, g = 0, b = 0;
    for (let i = 0; i < SST_STOPS.length - 1; i++) {
      const [v0, c0] = SST_STOPS[i];
      const [v1, c1] = SST_STOPS[i + 1];
      if (f >= v0 && f <= v1) {
        const t = (f - v0) / (v1 - v0);
        r = Math.round(c0[0] + t * (c1[0] - c0[0]));
        g = Math.round(c0[1] + t * (c1[1] - c0[1]));
        b = Math.round(c0[2] + t * (c1[2] - c0[2]));
        break;
      }
    }
    lut.push({ fahrenheit: f, r, g, b });
  }
  return lut;
})();

export function invertSstColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestF: number | null = null;
  for (let i = 0; i < SST_LUT.length; i++) {
    const entry = SST_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestF = entry.fahrenheit;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestF;
}

// -------------------------------------------------------------
// Liquid Precipitation Accumulation (0.01" to 10.0")
// -------------------------------------------------------------
const PRECIP_ACCUM_STOPS: [number, [number, number, number]][] = [
  [0.01, [154, 230, 180]],
  [0.10, [56, 161, 105]],
  [0.25, [0, 184, 212]],
  [0.50, [41, 98, 255]],
  [1.00, [124, 77, 255]],
  [2.00, [216, 27, 96]],
  [4.00, [244, 81, 30]],
  [7.00, [255, 214, 0]],
  [10.00, [255, 255, 255]]
];

const PRECIP_ACCUM_LUT: { inches: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let i = 0; i < PRECIP_ACCUM_STOPS.length - 1; i++) {
    const [v0, c0] = PRECIP_ACCUM_STOPS[i];
    const [v1, c1] = PRECIP_ACCUM_STOPS[i + 1];
    const steps = 20;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const inches = v0 + t * (v1 - v0);
      const r = Math.round(c0[0] + t * (c1[0] - c0[0]));
      const g = Math.round(c0[1] + t * (c1[1] - c0[1]));
      const b = Math.round(c0[2] + t * (c1[2] - c0[2]));
      lut.push({ inches, r, g, b });
    }
  }
  return lut;
})();

export function invertPrecipAccumColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestInches: number | null = null;
  for (let i = 0; i < PRECIP_ACCUM_LUT.length; i++) {
    const entry = PRECIP_ACCUM_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestInches = entry.inches;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestInches;
}

// -------------------------------------------------------------
// Snowfall Accumulation (0.1" to 30.0")
// -------------------------------------------------------------
const SNOW_ACCUM_STOPS: [number, [number, number, number]][] = [
  [0.1, [120, 210, 255]],
  [0.5, [70, 180, 255]],
  [1.5, [30, 140, 235]],
  [4.0, [80, 70, 210]],
  [8.0, [150, 50, 210]],
  [16.0, [210, 80, 225]],
  [30.0, [245, 180, 255]]
];

const SNOW_ACCUM_LUT: { inches: number; r: number; g: number; b: number }[] = (() => {
  const lut = [];
  for (let i = 0; i < SNOW_ACCUM_STOPS.length - 1; i++) {
    const [v0, c0] = SNOW_ACCUM_STOPS[i];
    const [v1, c1] = SNOW_ACCUM_STOPS[i + 1];
    const steps = 20;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const inches = v0 + t * (v1 - v0);
      const r = Math.round(c0[0] + t * (c1[0] - c0[0]));
      const g = Math.round(c0[1] + t * (c1[1] - c0[1]));
      const b = Math.round(c0[2] + t * (c1[2] - c0[2]));
      lut.push({ inches, r, g, b });
    }
  }
  return lut;
})();

export function invertSnowAccumColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestInches: number | null = null;
  for (let i = 0; i < SNOW_ACCUM_LUT.length; i++) {
    const entry = SNOW_ACCUM_LUT[i];
    const dr = r - entry.r;
    const dg = g - entry.g;
    const db = b - entry.b;
    const distSq = dr * dr + dg * dg + db * db;
    if (distSq < minDistance) {
      minDistance = distSq;
      bestInches = entry.inches;
      if (distSq === 0) break;
    }
  }
  if (minDistance > 2500) return null;
  return bestInches;
}

// -------------------------------------------------------------
// Snow Depth (0.5" to 120"+)
// -------------------------------------------------------------
const SNOW_DEPTH_STOPS: [number, [number, number, number]][] = [
  [0.5, [56, 189, 248]],
  [6.0, [2, 132, 199]],
  [24.0, [30, 64, 175]],
  [60.0, [55, 48, 163]],
  [120.0, [162, 28, 175]],
  [180.0, [255, 255, 255]]
];

export function invertSnowDepthColor(r: number, g: number, b: number): number | null {
  let minDistance = Infinity;
  let bestDepth: number | null = null;
  for (let i = 0; i < SNOW_DEPTH_STOPS.length - 1; i++) {
    const [v0, c0] = SNOW_DEPTH_STOPS[i];
    const [v1, c1] = SNOW_DEPTH_STOPS[i + 1];
    for (let s = 0; s <= 20; s++) {
      const t = s / 20.0;
      const depth = v0 + t * (v1 - v0);
      const pr = Math.round(c0[0] + t * (c1[0] - c0[0]));
      const pg = Math.round(c0[1] + t * (c1[1] - c0[1]));
      const pb = Math.round(c0[2] + t * (c1[2] - c0[2]));
      const distSq = (r - pr) * (r - pr) + (g - pg) * (g - pg) + (b - pb) * (b - pb);
      if (distSq < minDistance) {
        minDistance = distSq;
        bestDepth = depth;
      }
    }
  }
  if (minDistance > 3000) return null;
  return bestDepth;
}

// -------------------------------------------------------------
// Lenticular Mountain Wave Potential (0 - 100%)
// -------------------------------------------------------------
export function invertLenticularColor(r: number, g: number, b: number): number | null {
  if (b >= 200 && g >= 180) {
    const t = Math.max(0, Math.min(1, r / 255.0));
    return Math.round(t * 100);
  }
  if (r >= 220) {
    const t = Math.max(0, Math.min(1, (159 - g) / (159 - 82)));
    return Math.round(t * 100);
  }
  return null;
}

// -------------------------------------------------------------
// Wild Mushroom Harvest Conditions (0 - 100)
// -------------------------------------------------------------
export function invertMushroomColor(r: number, g: number, b: number): number | null {
  if (g > r && g > b) {
    const t = Math.max(0, Math.min(1, (g - 140) / 40.0));
    return Math.round(15 + t * 20);
  }
  if (r >= 190 && g >= 150) {
    const t = Math.max(0, Math.min(1, (r - 200) / 45.0));
    return Math.round(35 + t * 30);
  }
  if (r >= 230 && g >= 90) {
    const t = Math.max(0, Math.min(1, (140 - g) / 50.0));
    return Math.round(65 + t * 20);
  }
  if (r >= 200 && g < 80) {
    const t = Math.max(0, Math.min(1, (r - 220) / 35.0));
    return Math.round(85 + t * 15);
  }
  return null;
}

// -------------------------------------------------------------
// Fall Foliage Progression Index (FPI & Stage)
// -------------------------------------------------------------
export function invertFoliageColor(r: number, g: number, b: number): { fpi: number; stage: string } | null {
  const stages = [
    { name: 'Early Color', fpi: 15, r: 46, g: 125, b: 50 },
    { name: 'Moderate Color', fpi: 40, r: 251, g: 192, b: 45 },
    { name: 'Near Peak', fpi: 68, r: 245, g: 124, b: 0 },
    { name: 'Peak Foliage', fpi: 92, r: 211, g: 47, b: 47 },
    { name: 'Past Peak', fpi: 120, r: 121, g: 85, b: 72 },
    { name: 'Bare / Dormant', fpi: 140, r: 120, g: 144, b: 156 }
  ];
  let minDistance = Infinity;
  let bestStage: { fpi: number; stage: string } | null = null;
  for (const s of stages) {
    const distSq = (r - s.r) * (r - s.r) + (g - s.g) * (g - s.g) + (b - s.b) * (b - s.b);
    if (distSq < minDistance) {
      minDistance = distSq;
      bestStage = { fpi: s.fpi, stage: s.name };
    }
  }
  if (minDistance > 3500) return null;
  return bestStage;
}

export function invertCloudsColor(_r: number, _g: number, _b: number, a: number, level: string = 'total'): number | null {
  if (a < 10) return null;
  let alphaPower = 1.15;
  let maxAlpha = 250.0;
  if (level === 'low') {
    alphaPower = 1.3;
    maxAlpha = 235.0;
  } else if (level === 'mid') {
    alphaPower = 1.2;
    maxAlpha = 235.0;
  } else if (level === 'high') {
    alphaPower = 1.15;
    maxAlpha = 235.0;
  }
  const normA = Math.min(1.0, a / maxAlpha);
  const valNorm = Math.pow(normA, 1.0 / alphaPower);
  const pct = Math.min(100, Math.max(0, Math.round(valNorm * 100)));
  return pct;
}

export interface SampledPixel {
  r: number;
  g: number;
  b: number;
  a: number;
}

export class TilePixelSampler {
  private static instance: TilePixelSampler | null = null;
  private tileCache: Map<string, ImageData> = new Map();
  private pendingFetches: Map<string, Promise<ImageData | null>> = new Map();
  private offscreenCanvas: HTMLCanvasElement;
  private offscreenCtx: CanvasRenderingContext2D | null;

  constructor() {
    this.offscreenCanvas = document.createElement('canvas');
    this.offscreenCanvas.width = 256;
    this.offscreenCanvas.height = 256;
    this.offscreenCtx = this.offscreenCanvas.getContext('2d', { willReadFrequently: true });
  }

  public static getInstance(): TilePixelSampler {
    if (!this.instance) {
      this.instance = new TilePixelSampler();
    }
    return this.instance;
  }

  public clearCache(): void {
    this.tileCache.clear();
    this.pendingFetches.clear();
  }

  private isSingleImageWebpUrl(tileUrl: string): boolean {
    const cleanPath = (tileUrl || '').split('?')[0];
    return (
      cleanPath.endsWith('.webp') &&
      !cleanPath.includes('{z}') &&
      !/\/\d+\/\d+\/\d+\.webp$/i.test(cleanPath)
    );
  }

  private async sampleSingleImageWebp(
    tileUrl: string,
    lon: number,
    lat: number
  ): Promise<SampledPixel | null> {
    let bitmap = imageBitmapCache.get(tileUrl);
    if (!bitmap) {
      bitmap = await imageBitmapCache.load(tileUrl);
    }
    if (!bitmap || !this.offscreenCtx) {
      return null;
    }

    let coords = imageBitmapCache.getCachedBounds(tileUrl);
    if (!coords) {
      coords = await imageBitmapCache.loadBoundsForWebpUrl(tileUrl);
    }
    if (!coords) {
      const cleanPath = tileUrl.split('?')[0];
      const isGlobal = /_(?:gfs|ecmwf|gdps|icon13|icon|hgefs|dafs|gefs)(?:_|\.|$)/i.test(cleanPath);
      coords = isGlobal ? GLOBAL_COORDINATES : STANDARD_NA_COORDINATES;
    }

    const west = coords[0][0];
    const north = coords[0][1];
    const east = coords[2][0];
    const south = coords[2][1];

    const normLon = ((((lon + 180) % 360) + 360) % 360) - 180;
    if (normLon < west || normLon > east || lat < south || lat > north) {
      return null;
    }

    const mercY = (deg: number): number => {
      const rad = (Math.max(-85.051129, Math.min(85.051129, deg)) * Math.PI) / 180;
      const sin = Math.sin(rad);
      return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
    };

    const u = (normLon - west) / (east - west);
    let v: number;
    if (isAlreadyMercatorProjected(bitmap.width, bitmap.height, coords)) {
      const yNorth = mercY(north);
      const ySouth = mercY(south);
      const yLat = mercY(lat);
      v = (yLat - yNorth) / (ySouth - yNorth);
    } else {
      v = (north - lat) / (north - south);
    }

    if (u < 0 || u > 1 || v < 0 || v > 1) {
      return null;
    }

    const px = Math.max(0, Math.min(bitmap.width - 1, Math.floor(u * bitmap.width)));
    const py = Math.max(0, Math.min(bitmap.height - 1, Math.floor(v * bitmap.height)));

    this.offscreenCtx.clearRect(0, 0, 1, 1);
    this.offscreenCtx.drawImage(bitmap, px, py, 1, 1, 0, 0, 1, 1);
    const data = this.offscreenCtx.getImageData(0, 0, 1, 1).data;
    return {
      r: data[0],
      g: data[1],
      b: data[2],
      a: data[3]
    };
  }

  /**
   * Samples the raw unblended RGBA pixel at geographic coordinate (lon, lat)
   * for the given raster tile URL and zoom level.
   */
  public async samplePixel(
    tileUrl: string,
    lon: number,
    lat: number,
    zoom: number
  ): Promise<SampledPixel | null> {
    if (this.isSingleImageWebpUrl(tileUrl)) {
      const singlePixel = await this.sampleSingleImageWebp(tileUrl, lon, lat);
      if (singlePixel) {
        return singlePixel;
      }
      const cleanPath = tileUrl.split('?')[0];
      tileUrl = buildPmtilesUrl(cleanPath.replace(/_(?:1024|2048|4096)\.webp$/i, '.pmtiles'));
    }

    const n = Math.pow(2, zoom);
    const tx = Math.floor(((lon + 180) / 360) * n);
    const latRad = (lat * Math.PI) / 180;
    const ty = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);

    if (tx < 0 || tx >= n || ty < 0 || ty >= n) {
      return null;
    }

    const tileData = await this.getTileImageData(tileUrl, zoom, tx, ty);
    if (!tileData) {
      return null;
    }

    const tileWorldDim = n * 256;
    const worldX = ((lon + 180) / 360) * tileWorldDim;
    const sinLat = Math.sin(latRad);
    const worldY = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * tileWorldDim;

    const px = Math.max(0, Math.min(255, Math.floor(worldX - tx * 256)));
    const py = Math.max(0, Math.min(255, Math.floor(worldY - ty * 256)));

    const idx = (py * 256 + px) * 4;
    return {
      r: tileData.data[idx],
      g: tileData.data[idx + 1],
      b: tileData.data[idx + 2],
      a: tileData.data[idx + 3]
    };
  }

  private async getTileImageData(
    tileUrl: string,
    z: number,
    x: number,
    y: number
  ): Promise<ImageData | null> {
    const cacheKey = `${tileUrl}|${z}|${x}|${y}`;
    if (this.tileCache.has(cacheKey)) {
      return this.tileCache.get(cacheKey)!;
    }

    if (this.pendingFetches.has(cacheKey)) {
      return this.pendingFetches.get(cacheKey)!;
    }

    const fetchPromise = (async () => {
      try {
        let blob: Blob | null = null;
        if (tileUrl.startsWith('pmtiles://') || tileUrl.includes('.pmtiles')) {
          const cleanPmtilesUrl = buildPmtilesUrl(tileUrl.replace(/\/{z}\/{x}\/{y}.*$/, ''));
          const pmtilesInstance = getOrCreatePMTiles(cleanPmtilesUrl);
          const response = await pmtilesInstance.getZxy(z, x, y);
          if (!response || !response.data) return null;
          blob = new Blob([response.data], { type: 'image/webp' });
        } else {
          const clean = tileUrl.includes('/{z}/{x}/{y}')
            ? tileUrl.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y))
            : `${tileUrl.replace(/\/$/, '')}/${z}/${x}/${y}.webp`;
          const resp = await fetch(clean);
          if (!resp.ok) return null;
          blob = await resp.blob();
        }

        if (!blob) return null;

        let imgData: ImageData | null = null;
        if (typeof createImageBitmap !== 'undefined') {
          const bitmap = await createImageBitmap(blob);
          if (!this.offscreenCtx) return null;
          this.offscreenCtx.clearRect(0, 0, 256, 256);
          this.offscreenCtx.drawImage(bitmap, 0, 0, 256, 256);
          imgData = this.offscreenCtx.getImageData(0, 0, 256, 256);
          bitmap.close();
        } else {
          imgData = await new Promise<ImageData | null>((resolve) => {
            const img = new Image();
            const objUrl = URL.createObjectURL(blob!);
            img.onload = () => {
              URL.revokeObjectURL(objUrl);
              if (!this.offscreenCtx) {
                resolve(null);
                return;
              }
              this.offscreenCtx.clearRect(0, 0, 256, 256);
              this.offscreenCtx.drawImage(img, 0, 0, 256, 256);
              resolve(this.offscreenCtx.getImageData(0, 0, 256, 256));
            };
            img.onerror = () => {
              URL.revokeObjectURL(objUrl);
              resolve(null);
            };
            img.src = objUrl;
          });
        }

        if (imgData) {
          // Bounded cache retention
          if (this.tileCache.size >= 32) {
            const firstKey = this.tileCache.keys().next().value;
            if (firstKey) this.tileCache.delete(firstKey);
          }
          this.tileCache.set(cacheKey, imgData);
        }
        return imgData;
      } catch (_err) {
        return null;
      } finally {
        this.pendingFetches.delete(cacheKey);
      }
    })();

    this.pendingFetches.set(cacheKey, fetchPromise);
    return fetchPromise;
  }
}

export default TilePixelSampler;
